/*
 * @forgerock/ping-javascript-sdk
 *
 * webauthn.ts
 *
 * Copyright (c) 2024 - 2026 Ping Identity Corporation. All rights reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
import { callbackType } from '@forgerock/sdk-types';
import { WebAuthnOutcome, WebAuthnOutcomeType, WebAuthnStepType } from './enums.js';
import { arrayBufferToString, parseCredentials, parsePubKeyArray, parseRelyingPartyId, } from './helpers.js';
/**
 * Utility for integrating a web browser's WebAuthn API.
 *
 * Example:
 *
 * ```js
 * // Determine if a step is a WebAuthn step
 * const stepType = WebAuthn.getWebAuthnStepType(step);
 * if (stepType === WebAuthnStepType.Registration) {
 *   // Register a new device
 *   await WebAuthn.register(step);
 * } else if (stepType === WebAuthnStepType.Authentication) {
 *   // Authenticate with a registered device
 *   await WebAuthn.authenticate(step);
 * }
 * ```
 *
 * Conditional mediation (passkey autofill) support:
 *
 * Conditional mediation is **server-driven** in this SDK via WebAuthn metadata (`meta.mediation`).
 *
 * ```js
 * // Optional: feature-detect conditional UI before attempting
 * const supportsConditionalUI = await WebAuthn.isConditionalMediationSupported();
 *
 * if (supportsConditionalUI) {
 *   const controller = new AbortController();
 *
 *   // Optional: provide a signal to cancel an in-flight request
 *   await WebAuthn.authenticate(step, controller.signal);
 * }
 * ```
 *
 * Notes:
 * - When server-driven mediation is `'conditional'`, an `AbortSignal` will be used.
 *   If you don't provide one, the SDK will create one.
 * - If conditional mediation is requested but not supported by the browser,
 *   `authenticate()` throws a `NotSupportedError` and sets the hidden WebAuthn outcome to `unsupported`.
 * - To enable passkey autofill, add `autocomplete="webauthn"` to your username field:
 *   `<input type="text" name="username" autocomplete="webauthn" />`
 */
export class WebAuthn {
    static conditionalAbortController;
    /**
     * Determines if the given step is a WebAuthn step.
     *
     * @param step The step to evaluate
     * @return A WebAuthnStepType value
     */
    static getWebAuthnStepType(step) {
        const outcomeCallback = this.getOutcomeCallback(step);
        const metadataCallback = this.getMetadataCallback(step);
        const textOutputCallback = this.getTextOutputCallback(step);
        if (outcomeCallback && metadataCallback) {
            const metadata = metadataCallback.getOutputValue('data');
            if (metadata?.pubKeyCredParams) {
                return WebAuthnStepType.Registration;
            }
            return WebAuthnStepType.Authentication;
        }
        if (outcomeCallback && textOutputCallback) {
            const message = textOutputCallback.getMessage();
            if (message.includes('pubKeyCredParams')) {
                return WebAuthnStepType.Registration;
            }
            return WebAuthnStepType.Authentication;
        }
        else {
            return WebAuthnStepType.None;
        }
    }
    /**
     * Determines if the browser supports conditional mediation.
     *
     * @return Whether the browser supports conditional mediation
     */
    static async isConditionalMediationSupported() {
        return (typeof PublicKeyCredential !== 'undefined' &&
            typeof PublicKeyCredential.isConditionalMediationAvailable === 'function' &&
            (await PublicKeyCredential.isConditionalMediationAvailable()));
    }
    /**
     * Populates the step with the necessary authentication outcome.
     *
     * @param step The step that contains WebAuthn authentication data
     * @param signal Optional AbortSignal passed through to `navigator.credentials.get()`
     * @return The populated step
     */
    static async authenticate(step, signal) {
        const { hiddenCallback, metadataCallback, textOutputCallback } = this.getCallbacks(step);
        if (hiddenCallback && (metadataCallback || textOutputCallback)) {
            let outcome;
            let credential = null;
            let mediation;
            try {
                let publicKey;
                if (metadataCallback) {
                    const meta = metadataCallback.getOutputValue('data');
                    mediation = meta.mediation;
                    publicKey = this.createAuthenticationPublicKey(meta);
                    if (mediation === 'conditional') {
                        // Abort any prior conditional request started by the SDK.
                        // (If the caller provides their own signal, we still abort the prior SDK-owned one.)
                        this.conditionalAbortController?.abort();
                        const abortSignal = signal ?? this.createAbortController().signal;
                        const isConditionalMediationSupported = await this.isConditionalMediationSupported();
                        if (!isConditionalMediationSupported) {
                            const e = new Error('Conditional mediation was requested, but is not supported by this browser.');
                            e.name = WebAuthnOutcomeType.NotSupportedError;
                            throw e;
                        }
                        credential = await this.getAuthenticationCredential(publicKey, mediation, abortSignal);
                        outcome = this.getAuthenticationOutcome(credential);
                    }
                    else {
                        credential = await this.getAuthenticationCredential(publicKey, mediation, signal);
                        outcome = this.getAuthenticationOutcome(credential);
                    }
                }
                else {
                    throw new Error('No metadata callback found for WebAuthn authentication. Please disable JavaScript in server node.');
                }
            }
            catch (error) {
                if (!(error instanceof Error))
                    throw error;
                // In conditional mediation flows, the app may abort an in-flight request when the user
                // submits a different method or the step changes. Treat this as cancellation and do not
                // mutate the hidden outcome.
                if (mediation === 'conditional' && error.name === 'AbortError') {
                    throw error;
                }
                // NotSupportedError is a special case
                if (error.name === WebAuthnOutcomeType.NotSupportedError) {
                    hiddenCallback.setInputValue(WebAuthnOutcome.Unsupported);
                    throw error;
                }
                hiddenCallback.setInputValue(`${WebAuthnOutcome.Error}::${error.name}:${error.message}`);
                throw error;
            }
            if (metadataCallback) {
                const meta = metadataCallback.getOutputValue('data');
                if (meta?.supportsJsonResponse && credential && 'authenticatorAttachment' in credential) {
                    hiddenCallback.setInputValue(JSON.stringify({
                        authenticatorAttachment: credential.authenticatorAttachment,
                        legacyData: outcome,
                    }));
                    return step;
                }
            }
            hiddenCallback.setInputValue(outcome);
            return step;
        }
        else {
            const e = new Error('Incorrect callbacks for WebAuthn authentication');
            e.name = WebAuthnOutcomeType.DataError;
            hiddenCallback?.setInputValue(`${WebAuthnOutcome.Error}::${e.name}:${e.message}`);
            throw e;
        }
    }
    /**
     * Populates the step with the necessary registration outcome.
     *
     * @param step The step that contains WebAuthn registration data
     * @return The populated step
     */
    // Can make this generic const in Typescript 5.0 > and the name itself will
    // be inferred from the type so `typeof deviceName` will not just return string
    // but the actual name of the deviceName passed in as a generic.
    static async register(step, deviceName) {
        const { hiddenCallback, metadataCallback, textOutputCallback } = this.getCallbacks(step);
        if (hiddenCallback && (metadataCallback || textOutputCallback)) {
            let outcome;
            let credential = null;
            try {
                let publicKey;
                if (metadataCallback) {
                    const meta = metadataCallback.getOutputValue('data');
                    publicKey = this.createRegistrationPublicKey(meta);
                    credential = await this.getRegistrationCredential(publicKey);
                    outcome = this.getRegistrationOutcome(credential);
                }
                else {
                    throw new Error('No metadata callback found for WebAuthn registration. Please disable JavaScript in server node.');
                }
            }
            catch (error) {
                if (!(error instanceof Error))
                    throw error;
                // NotSupportedError is a special case
                if (error.name === WebAuthnOutcomeType.NotSupportedError) {
                    hiddenCallback.setInputValue(WebAuthnOutcome.Unsupported);
                    throw error;
                }
                hiddenCallback.setInputValue(`${WebAuthnOutcome.Error}::${error.name}:${error.message}`);
                throw error;
            }
            if (metadataCallback) {
                const meta = metadataCallback.getOutputValue('data');
                if (meta?.supportsJsonResponse && credential && 'authenticatorAttachment' in credential) {
                    hiddenCallback.setInputValue(JSON.stringify({
                        authenticatorAttachment: credential.authenticatorAttachment,
                        legacyData: deviceName && deviceName.length > 0 ? `${outcome}::${deviceName}` : outcome,
                    }));
                    return step;
                }
            }
            hiddenCallback.setInputValue(deviceName && deviceName.length > 0 ? `${outcome}::${deviceName}` : outcome);
            return step;
        }
        else {
            const e = new Error('Incorrect callbacks for WebAuthn registration');
            e.name = WebAuthnOutcomeType.DataError;
            hiddenCallback?.setInputValue(`${WebAuthnOutcome.Error}::${e.name}:${e.message}`);
            throw e;
        }
    }
    /**
     * Returns an object containing the two WebAuthn callbacks.
     *
     * @param step The step that contains WebAuthn callbacks
     * @return The WebAuthn callbacks
     */
    static getCallbacks(step) {
        const hiddenCallback = this.getOutcomeCallback(step);
        const metadataCallback = this.getMetadataCallback(step);
        const textOutputCallback = this.getTextOutputCallback(step);
        const returnObj = {
            hiddenCallback,
        };
        if (metadataCallback) {
            returnObj.metadataCallback = metadataCallback;
        }
        else if (textOutputCallback) {
            returnObj.textOutputCallback = textOutputCallback;
        }
        return returnObj;
    }
    /**
     * Returns the WebAuthn metadata callback containing data to pass to the browser
     * Web Authentication API.
     *
     * @param step The step that contains WebAuthn callbacks
     * @return The metadata callback
     */
    static getMetadataCallback(step) {
        return step.getCallbacksOfType(callbackType.MetadataCallback).find((x) => {
            const cb = x.getOutputByName('data', undefined);
            // eslint-disable-next-line no-prototype-builtins
            return cb && cb.hasOwnProperty('relyingPartyId');
        });
    }
    /**
     * Returns the WebAuthn hidden value callback where the outcome should be populated.
     *
     * @param step The step that contains WebAuthn callbacks
     * @return The hidden value callback
     */
    static getOutcomeCallback(step) {
        return step
            .getCallbacksOfType(callbackType.HiddenValueCallback)
            .find((x) => x.getOutputByName('id', '') === 'webAuthnOutcome');
    }
    /**
     * Returns the WebAuthn metadata callback containing data to pass to the browser
     * Web Authentication API.
     *
     * @param step The step that contains WebAuthn callbacks
     * @return The metadata callback
     */
    static getTextOutputCallback(step) {
        return step
            .getCallbacksOfType(callbackType.TextOutputCallback)
            .find((x) => {
            const cb = x.getOutputByName('message', undefined);
            return cb && cb.includes('webAuthnOutcome');
        });
    }
    /**
     * Retrieves the credential from the browser Web Authentication API.
     *
     * @param options The public key options associated with the request
     * @param mediation Optional mediation requirement passed through to `navigator.credentials.get()`
     * @param signal Optional AbortSignal passed through to `navigator.credentials.get()`
     * @return The credential
     */
    static async getAuthenticationCredential(options, mediation, signal) {
        // Feature check before we attempt registering a device
        if (!window.PublicKeyCredential) {
            const e = new Error('PublicKeyCredential not supported by this browser');
            e.name = WebAuthnOutcomeType.NotSupportedError;
            throw e;
        }
        const credential = await navigator.credentials.get({
            publicKey: options,
            ...(mediation && { mediation }),
            ...(signal && { signal }),
        });
        return credential;
    }
    /**
     * Converts an authentication credential into the outcome expected by OpenAM.
     *
     * @param credential The credential to convert
     * @return The outcome string
     */
    static getAuthenticationOutcome(credential) {
        if (credential === null) {
            const e = new Error('No credential generated from authentication');
            e.name = WebAuthnOutcomeType.UnknownError;
            throw e;
        }
        try {
            const clientDataJSON = arrayBufferToString(credential.response.clientDataJSON);
            const assertionResponse = credential.response;
            const authenticatorData = new Int8Array(assertionResponse.authenticatorData).toString();
            const signature = new Int8Array(assertionResponse.signature).toString();
            // Current native typing for PublicKeyCredential does not include `userHandle`
            // eslint-disable-next-line
            // @ts-ignore
            const userHandle = arrayBufferToString(credential.response.userHandle);
            let stringOutput = `${clientDataJSON}::${authenticatorData}::${signature}::${credential.id}`;
            // Check if Username is stored on device
            if (userHandle) {
                stringOutput = `${stringOutput}::${userHandle}`;
                return stringOutput;
            }
            return stringOutput;
        }
        catch {
            const e = new Error('Transforming credential object to string failed');
            e.name = WebAuthnOutcomeType.EncodingError;
            throw e;
        }
    }
    /**
     * Retrieves the credential from the browser Web Authentication API.
     *
     * @param options The public key options associated with the request
     * @return The credential
     */
    static async getRegistrationCredential(options) {
        // Feature check before we attempt registering a device
        if (!window.PublicKeyCredential) {
            const e = new Error('PublicKeyCredential not supported by this browser');
            e.name = WebAuthnOutcomeType.NotSupportedError;
            throw e;
        }
        const credential = await navigator.credentials.create({
            publicKey: options,
        });
        return credential;
    }
    /**
     * Converts a registration credential into the outcome expected by OpenAM.
     *
     * @param credential The credential to convert
     * @return The outcome string
     */
    static getRegistrationOutcome(credential) {
        if (credential === null) {
            const e = new Error('No credential generated from registration');
            e.name = WebAuthnOutcomeType.UnknownError;
            throw e;
        }
        try {
            const clientDataJSON = arrayBufferToString(credential.response.clientDataJSON);
            const attestationResponse = credential.response;
            const attestationObject = new Int8Array(attestationResponse.attestationObject).toString();
            return `${clientDataJSON}::${attestationObject}::${credential.id}`;
        }
        catch {
            const e = new Error('Transforming credential object to string failed');
            e.name = WebAuthnOutcomeType.EncodingError;
            throw e;
        }
    }
    /**
     * Converts authentication tree metadata into options required by the browser
     * Web Authentication API.
     *
     * @param metadata The metadata provided in the authentication tree MetadataCallback
     * @return The Web Authentication API request options
     */
    static createAuthenticationPublicKey(metadata) {
        const { acceptableCredentials, allowCredentials, challenge, relyingPartyId, timeout, userVerification, } = metadata;
        const rpId = parseRelyingPartyId(relyingPartyId);
        const allowCredentialsValue = parseCredentials(allowCredentials || acceptableCredentials || '');
        return {
            challenge: Uint8Array.from(atob(challenge), (c) => c.charCodeAt(0)).buffer,
            timeout,
            // only add key-value pair if proper value is provided
            ...(allowCredentialsValue?.length ? { allowCredentials: allowCredentialsValue } : {}),
            ...(userVerification && { userVerification }),
            ...(rpId && { rpId }),
        };
    }
    /**
     * Converts authentication tree metadata into options required by the browser
     * Web Authentication API.
     *
     * @param metadata The metadata provided in the authentication tree MetadataCallback
     * @return The Web Authentication API request options
     */
    static createRegistrationPublicKey(metadata) {
        const { pubKeyCredParams: pubKeyCredParamsString } = metadata;
        const pubKeyCredParams = parsePubKeyArray(pubKeyCredParamsString);
        if (!pubKeyCredParams) {
            const e = new Error('Missing pubKeyCredParams property from registration options');
            e.name = WebAuthnOutcomeType.DataError;
            throw e;
        }
        const excludeCredentials = parseCredentials(metadata.excludeCredentials);
        const { attestationPreference, authenticatorSelection, challenge, relyingPartyId, relyingPartyName, timeout, userId, userName, displayName, } = metadata;
        const rpId = parseRelyingPartyId(relyingPartyId);
        const rp = {
            name: relyingPartyName,
            ...(rpId && { id: rpId }),
        };
        return {
            attestation: attestationPreference,
            authenticatorSelection: JSON.parse(authenticatorSelection),
            challenge: Uint8Array.from(atob(challenge), (c) => c.charCodeAt(0)).buffer,
            ...(excludeCredentials.length && { excludeCredentials }),
            pubKeyCredParams,
            rp,
            timeout,
            user: {
                displayName: displayName || userName,
                id: Int8Array.from(userId.split('').map((c) => c.charCodeAt(0))),
                name: displayName || userName,
            },
        };
    }
    /**
     * Creates and stores an SDK-owned {@link AbortController} for conditional mediation,
     * aborting any previous SDK-owned controller first.
     *
     * @return A new AbortController for conditional mediation.
     */
    static createAbortController() {
        this.conditionalAbortController?.abort();
        const abortController = new AbortController();
        this.conditionalAbortController = abortController;
        return abortController;
    }
}
export { WebAuthnOutcome, WebAuthnStepType };
